'use client';

import { useEffect, useSyncExternalStore } from 'react';
import {
  useMarketplaceSource,
  type MarketplaceCart,
  type MarketplaceSource,
} from '@akademi/site-builder-runtime';

export interface MarketplaceCartController {
  source: MarketplaceSource;
  cart: MarketplaceCart | null;
  status: 'loading' | 'ready' | 'error';
  /** Add one licence of a course/catalog id, then refresh. */
  add(marketplaceItemId: string): Promise<void>;
  /** Remove a cart row by its `cartItemId`, then refresh. */
  remove(cartItemId: string): Promise<void>;
  /** Remove whichever cart row holds `marketplaceItemId` (listing grids only
   *  know the item id, not the cart-row id). No-op if it isn't in the cart. */
  removeByItemId(marketplaceItemId: string): Promise<void>;
  applyCoupon(hashId: string): Promise<void>;
  removeCoupon(couponId: string): Promise<void>;
  refresh(): Promise<void>;
  inCart(marketplaceItemId: string): boolean;
  /** The payment gateway chosen in {@link MarketplacePaymentMethod} — shared
   *  so {@link MarketplaceCheckoutCta} requests a redirect for the gateway
   *  the user actually picked, not a hardcoded default. `null` until the
   *  user (or an auto-select of the only option) picks one. */
  selectedGateway: string | null;
  setSelectedGateway(gateway: string | null): void;
}

interface CartState {
  cart: MarketplaceCart | null;
  status: 'loading' | 'ready' | 'error';
  selectedGateway: string | null;
}

/**
 * Module-level store backing {@link useMarketplaceCart} — every marketplace
 * block on a page (stepper, items, summary, coupon, payment method, address,
 * checkout CTA, …) calls the hook independently with no common React
 * ancestor to hold shared state in, so the state has to live outside any one
 * component: a coupon applied in one block must be visible in the summary
 * block, and the gateway picked in `MarketplacePaymentMethod` must reach
 * `MarketplaceCheckoutCta`. `useMarketplaceSource()` is already one shared
 * context instance app-wide, so keying this off "the" source (there is only
 * ever one cart per browser session) is safe — no multi-tenant/SSR leakage
 * since this module only ever executes client-side per browser tab.
 */
class MarketplaceCartStore {
  private state: CartState = { cart: null, status: 'loading', selectedGateway: null };
  private listeners = new Set<() => void>();
  private source: MarketplaceSource | null = null;
  private initialized = false;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): CartState => this.state;

  private set(patch: Partial<CartState>) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  ensureInitialized(source: MarketplaceSource) {
    if (this.initialized && this.source === source) return;
    this.source = source;
    this.initialized = true;
    this.set({ status: 'loading' });
    source
      .getCart()
      .then((cart) => this.set({ cart, status: 'ready' }))
      .catch(() => this.set({ status: 'error' }));
  }

  async refresh(): Promise<void> {
    if (!this.source) return;
    try {
      const cart = await this.source.getCart();
      this.set({ cart, status: 'ready' });
    } catch {
      this.set({ status: 'error' });
    }
  }

  async apply(fn: () => Promise<MarketplaceCart>): Promise<void> {
    try {
      const cart = await fn();
      this.set({ cart, status: 'ready' });
    } catch (err) {
      // Leave the last-known cart on screen, but let the caller know it
      // failed — rethrow so a toast at the call site reports it instead of
      // silently showing success.
      await this.refresh();
      throw err;
    }
  }

  setSelectedGateway(gateway: string | null) {
    this.set({ selectedGateway: gateway });
  }
}

const store = new MarketplaceCartStore();

/**
 * Shared cart state for the marketplace blocks. Returns `null` when no
 * {@link useMarketplaceSource} provider is mounted — every caller then
 * falls back to its own local/demo state (the "fallbacks stay, for
 * testing" contract). When a source IS mounted, every block reads/writes
 * the same {@link MarketplaceCartStore} singleton, so a mutation in one
 * block (add to cart, apply a coupon, pick a gateway) is immediately visible
 * to every other marketplace block on the page.
 */
export function useMarketplaceCart(): MarketplaceCartController | null {
  const source = useMarketplaceSource();
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  useEffect(() => {
    if (source) store.ensureInitialized(source);
  }, [source]);

  if (!source) return null;

  return {
    source,
    cart: snapshot.cart,
    status: snapshot.status,
    add: (id) => store.apply(() => source.addToCart(id)),
    remove: (cartItemId) => store.apply(() => source.removeFromCart(cartItemId)),
    removeByItemId: (marketplaceItemId) => {
      const row = snapshot.cart?.items.find((i) => i.marketplaceItemId === marketplaceItemId);
      if (!row) return Promise.resolve();
      return store.apply(() => source.removeFromCart(row.cartItemId));
    },
    applyCoupon: (hashId) => store.apply(() => source.applyCoupon(hashId)),
    removeCoupon: (couponId) => store.apply(() => source.removeCoupon(couponId)),
    refresh: () => store.refresh(),
    inCart: (marketplaceItemId) =>
      Boolean(snapshot.cart?.items.some((i) => i.marketplaceItemId === marketplaceItemId)),
    selectedGateway: snapshot.selectedGateway,
    setSelectedGateway: (gateway) => store.setSelectedGateway(gateway),
  };
}
